import { useState, useMemo, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import {
  useGetAssembliesRiskAssessmentSummaryQuery,
  RiskType,
  Maybe,
} from '@/__generated__/graphql';
import { riskAssessmentAssemblyTemplate } from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { Cell, CellText, TickLabel, reviewTabStyles } from './styles';
import { useDataToGenerateMatrices } from '../hooks/useDataToGenerateMatrices';
import { businessLossKey, sheKey, xLabels, yLabels } from '@/constants';

type MatrixProps = {
  matrix: { row: string; column: string; count: number | undefined; color: string }[];
  text: string;
  maxSize: string;
};

export const Matrix = ({ matrix, text, maxSize }: MatrixProps) => {
  return (
    <Grid container sx={{ maxWidth: maxSize, maxHeight: maxSize, aspectRatio: '15 / 16' }}>
      <Grid container spacing={'0.5rem'} size={12}>
        <Grid size={2}>
          <Cell color="transparent">
            <TickLabel>COF</TickLabel>
          </Cell>
        </Grid>
        <Grid size={10} sx={{ alignItems: 'center', display: 'flex' }}>
          <TickLabel>{text}</TickLabel>
        </Grid>
        {matrix?.map((item, index) => (
          <>
            {index % 5 === 0 && (
              <Grid container key={`${text}-${item.row}-${item.column}-label`} size={2}>
                <Cell color="transparent">
                  <TickLabel>{item.row}</TickLabel>
                </Cell>
              </Grid>
            )}
            <Grid container key={`${text}-${item.row}-${item.column}`} size={2}>
              <Cell color={item.color}>
                <CellText>{item.count}</CellText>
              </Cell>
            </Grid>
          </>
        ))}
      </Grid>
      <Grid size={2}>
        <Cell color="transparent">
          <TickLabel>POF</TickLabel>
        </Cell>
      </Grid>
      {xLabels.map((label) => (
        <Grid key={label} size={2}>
          <Cell color="transparent">
            <TickLabel>{label}</TickLabel>
          </Cell>
        </Grid>
      ))}
    </Grid>
  );
};

export const Review = ({ selected }: { selected: boolean }) => {
  const template = useAtomValue(riskAssessmentAssemblyTemplate);
  const structureId = useGetStructureId();
  const [businessLoss, setBusinessLoss] = useState<Maybe<RiskType>[] | undefined>([]);
  const [she, setShe] = useState<Maybe<RiskType>[] | undefined>([]);

  const { data: riskSummaryData } = useGetAssembliesRiskAssessmentSummaryQuery({
    variables: {
      input: {
        structureId,
        templateId: template?.id!,
        templateFieldKeys: [businessLossKey, sheKey],
      },
    },
    fetchPolicy: 'no-cache',
  });

  const riskMatricesData = useMemo(() => {
    if (riskSummaryData) {
      return riskSummaryData?.getAssembliesRiskAssessmentSummary;
    }
    return undefined;
  }, [riskSummaryData]);

  useEffect(() => {
    if (riskMatricesData) {
      const riskBusinessLoss =
        riskMatricesData?.find((riskMatrixData) => {
          return riskMatrixData?.key === businessLossKey;
        })?.values ?? [];
      const riskShe =
        riskMatricesData?.find((riskMatrixData) => {
          return riskMatrixData?.key === sheKey;
        })?.values ?? [];
      setBusinessLoss(riskBusinessLoss);
      setShe(riskShe);
    }
  }, [riskMatricesData]);

  const businessLossMatrix = useDataToGenerateMatrices(yLabels, xLabels, businessLoss);
  const sheMatrix = useDataToGenerateMatrices(yLabels, xLabels, she);

  return (
    <Paper
      data-testid="review"
      elevation={0}
      sx={{ ...reviewTabStyles, display: selected ? 'block' : 'none' }}
    >
      <Stack
        direction="row"
        spacing={3}
        sx={{
          height: '100%',
          justifyContent: 'space-around',
          alignItems: 'center',
          p: 5,
        }}
      >
        <Matrix data-testid="she-matrix" matrix={sheMatrix} text="SHE Risk Matrix" maxSize="40vh" />
        <Matrix
          data-testid="business-loss-matrix"
          matrix={businessLossMatrix}
          text="Business Loss Risk Matrix"
          maxSize="40vh"
        />
      </Stack>
    </Paper>
  );
};
